import { AuthSessionManager } from "@app/AuthSessionManager.ts";
import type { AuthSessionRefresher } from "@app/refreshAuthSession.ts";
import { trpcUtils } from "@app/trpcUtils.ts";
import type { AppRouter } from "@server/routers/appRouter.ts";
import { TRPCClientError, type TRPCLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";

export const createTrpcRefreshAuthSessionLink =
  (refresh: AuthSessionRefresher): TRPCLink<AppRouter> =>
  () =>
  ({ next, op }) =>
    observable((observer) => {
      const snapshot = AuthSessionManager.getSnapshot();
      let isUnsubscribed = false;
      const sessionChanged = () =>
        new TRPCClientError<AppRouter>("Authentication session changed");
      const canRefresh =
        !op.path.startsWith("auth.") ||
        op.path === "auth.context" ||
        op.path === "auth.changePassword";

      const subscribe = (allowRefresh: boolean) =>
        next(op).subscribe({
          next(value) {
            if (!AuthSessionManager.isCurrent(snapshot)) {
              observer.error(sessionChanged());
              return;
            }
            observer.next(value);
          },
          async error(error) {
            if (!AuthSessionManager.isCurrent(snapshot)) {
              observer.error(sessionChanged());
              return;
            }
            if (!canRefresh || !trpcUtils.isUnauthorized(error)) {
              observer.error(error);
              return;
            }
            if (!allowRefresh) {
              AuthSessionManager.clear();
              observer.error(error);
              return;
            }
            try {
              const refreshed = await refresh(snapshot);
              if (isUnsubscribed) {
                return;
              }
              if (!refreshed || !AuthSessionManager.isCurrent(snapshot)) {
                observer.error(error);
                return;
              }
              currentSub = subscribe(false);
            } catch (refreshError) {
              if (!isUnsubscribed) {
                observer.error(
                  TRPCClientError.from<AppRouter>(
                    refreshError instanceof Error
                      ? refreshError
                      : new Error("Unable to refresh session"),
                  ),
                );
              }
            }
          },
          complete: () => observer.complete(),
        });

      let currentSub = subscribe(true);
      return () => {
        isUnsubscribed = true;
        currentSub.unsubscribe();
      };
    });
